import { Accordion, AccordionTab } from "primereact/accordion";

import { CodeDisplay } from "./CodeDisplay";
import { QUESTION_TYPES } from "sj-ui-toolkit/src/constants";

interface Choice {
  key?: string;
  choice?: string;
  code?: string[];
  start?: number;
  end?: number;
}

interface ScoreDetails {
  earned: boolean;
}

interface Topic {
  title: string;
}

interface Prompt {
  typeId?: string;
  configuration?: {
    prompt?: string;
    choices?: Choice[];
    code?: string | string[];
    expectedAnswer?: string;
    correctAnswer?: string;
  };
}

interface Question {
  prompt?: Prompt;
  submitted?: string | null;
  answers?: string[];
}

export interface ExamResult {
  score: ScoreDetails;
  topic: Topic;
  question?: Question;
  programmingLanguage?: string;
}

// ---------------------------------------------------------------------------
// Local helper functions (replicate admin-v2's knowledgeAssessments helpers)
// ---------------------------------------------------------------------------

const findChoiceByKey = (choices: Choice[], key?: string | null): string =>
  choices.find(c => c.key === key)?.choice ?? "-";

const findCodeSnippet = (choices: Choice[], key?: string | null): string =>
  choices.find(c => c.key === key)?.code?.[0] ?? "-";

const formatAnswerKey = (key?: string | null): string => key?.toUpperCase() ?? "-";

const getLineRange = (choices: Choice[], key?: string | null): number[] | null => {
  const choice = choices.find(c => c.key === key);
  if (!choice || choice.start == null || choice.end == null) return null;
  return Array.from({ length: choice.end - choice.start + 1 }, (_, i) => choice.start! + i);
};

const getLineNumber = (choices: Choice[], key?: string | null): number | null => {
  const choice = choices.find(c => c.key === key);
  return choice?.choice ? parseInt(choice.choice, 10) : null;
};

// ---------------------------------------------------------------------------
// AnswerDisplay
// ---------------------------------------------------------------------------

const AnswerDisplay = ({ result }: { result: ExamResult }) => {
  const { question } = result;
  if (!question) return null;
  const { submitted, answers, prompt } = question;
  const isUnanswered = submitted == null;
  const SKIPPED_ANSWER_TEXT = "Question was skipped or unanswered.";

  const configuration = prompt?.configuration ?? {};
  const choices = configuration.choices ?? [];
  const typeId = prompt?.typeId;

  const programmingLanguage = result.programmingLanguage ?? "python";
  const correctChoiceKey = answers?.[0];
  const incorrectChoiceKey = submitted && submitted !== correctChoiceKey ? submitted : null;

  let userAnswerText: React.ReactNode;
  let expectedAnswerText: React.ReactNode;
  let codeDisplay: React.ReactNode = null;

  switch (typeId) {
    case QUESTION_TYPES.multipleChoiceCode: {
      userAnswerText = isUnanswered ? (
        SKIPPED_ANSWER_TEXT
      ) : (
        <>
          {formatAnswerKey(submitted)}, which is:{" "}
          <code className="atlas-code bg-elevation-1">{findCodeSnippet(choices, submitted)}</code>
        </>
      );
      const expectedAnswerKey = answers?.[0];
      expectedAnswerText = (
        <>
          {formatAnswerKey(expectedAnswerKey)}, which is:{" "}
          <code className="atlas-code bg-elevation-1">
            {findCodeSnippet(choices, expectedAnswerKey)}
          </code>
        </>
      );
      break;
    }

    case QUESTION_TYPES.blockCodeSelection: {
      const userLines = isUnanswered ? [] : (getLineRange(choices, submitted) ?? []);
      const correctLines = getLineRange(choices, answers?.[0]) ?? [];

      userAnswerText = isUnanswered
        ? SKIPPED_ANSWER_TEXT
        : userLines.length > 0
          ? `${formatAnswerKey(submitted)}, which is lines ${userLines[0]}-${userLines[userLines.length - 1]}`
          : "Invalid selection";

      expectedAnswerText =
        correctLines.length > 0
          ? `${formatAnswerKey(answers?.[0])}, which is lines ${correctLines[0]}-${correctLines[correctLines.length - 1]}`
          : "Answer not available";

      codeDisplay = configuration.code ? (
        <CodeDisplay
          code={configuration.code}
          language={programmingLanguage}
          userSelectedLines={userLines}
          correctLines={correctLines}
          choices={choices}
          correctChoiceKey={correctChoiceKey}
          incorrectChoiceKey={incorrectChoiceKey}
        />
      ) : null;
      break;
    }

    case QUESTION_TYPES.singleLineCodeSelection: {
      const userLine = isUnanswered ? null : getLineNumber(choices, submitted);
      const correctLine = getLineNumber(choices, answers?.[0]);

      userAnswerText = isUnanswered
        ? SKIPPED_ANSWER_TEXT
        : userLine
          ? `${formatAnswerKey(submitted)}, which is line ${userLine}`
          : "Invalid selection";

      expectedAnswerText = correctLine
        ? `${formatAnswerKey(answers?.[0])}, which is line ${correctLine}`
        : "Answer not available";

      codeDisplay = configuration.code ? (
        <CodeDisplay
          code={configuration.code}
          language={programmingLanguage}
          userSelectedLines={userLine ? [userLine] : []}
          correctLines={correctLine ? [correctLine] : []}
          choices={choices}
          correctChoiceKey={correctChoiceKey}
          incorrectChoiceKey={incorrectChoiceKey}
        />
      ) : null;
      break;
    }

    default: {
      userAnswerText = isUnanswered
        ? SKIPPED_ANSWER_TEXT
        : choices.length > 0
          ? `${formatAnswerKey(submitted)}: ${findChoiceByKey(choices, submitted)}`
          : (submitted ?? "No answer provided");

      const expectedAnswerKey = answers?.[0];
      expectedAnswerText =
        expectedAnswerKey && choices.length > 0
          ? `${formatAnswerKey(expectedAnswerKey)}: ${findChoiceByKey(choices, expectedAnswerKey)}`
          : (expectedAnswerKey ??
            configuration.expectedAnswer ??
            configuration.correctAnswer ??
            "Answer not available");
      break;
    }
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div>
          <p className="body-sm text-secondary mb-2">Your Answer:</p>
          <p>{userAnswerText}</p>
        </div>
        <div>
          <p className="body-sm text-secondary mb-2">Expected Answer:</p>
          <p>{expectedAnswerText}</p>
        </div>
      </div>
      {codeDisplay}
    </>
  );
};

// ---------------------------------------------------------------------------
// QuestionsResult
// ---------------------------------------------------------------------------

interface QuestionsResultProps {
  results: ExamResult[];
  activeIndex: number | number[];
  setActiveIndex: (index: number | number[]) => void;
}

const QuestionsResult = ({ results, activeIndex, setActiveIndex }: QuestionsResultProps) => {
  if (!results.length) return null;

  return (
    <div className="flex-1 overflow-y-auto">
      <Accordion
        multiple
        activeIndex={activeIndex as number[]}
        expandIcon={<i className="pi pi-angle-left" />}
        collapseIcon={<i className="pi pi-angle-down" />}
        onTabChange={e => setActiveIndex(e.index)}
        aria-label="Assessment questions"
      >
        {results.map((result, index) => (
          <AccordionTab
            key={index}
            headerTemplate={
              <div className="flex items-center gap-3 mr-auto">
                <i className={`flex-none ${result.score.earned ? "pi-sj-pass" : "pi-sj-fail"}`} />
                <h5 className="atlas-h5">{`Question ${index + 1}: ${result.topic.title}`}</h5>
              </div>
            }
            pt={{ headerAction: { className: "flex flex-row-reverse" } }}
          >
            <div>
              <p className="body-lg">{result.question?.prompt?.configuration?.prompt ?? "N/A"}</p>
              <AnswerDisplay result={result} />
            </div>
          </AccordionTab>
        ))}
      </Accordion>
    </div>
  );
};

export default QuestionsResult;
